import { usePlatform } from '~/composables/usePlatform'

/**
 * Discord-like "away" detection for presence. The server shows a user as idle («Нет на месте»)
 * when every one of their hub connections reports idle — and only if they picked «Онлайн»
 * (DND and invisible are left alone). This module decides what *this* device reports:
 *
 * - desktop: no mouse / keyboard / touch / wheel input for {@link IDLE_AFTER_MS};
 * - iOS/Android: the app is in the background (hidden page), right away.
 *
 * Closing or quitting the app isn't handled here at all: the hub sockets drop with it, and the
 * server takes the user offline itself.
 *
 * Module-level singleton: `useGroupsSignalR` watches `isIdle` and reports every change (and the
 * current value on every (re)connect) to the server, the own-avatar dot reads it too.
 */

/** Same threshold Discord uses. */
export const IDLE_AFTER_MS = 10 * 60 * 1000
// Activity events arrive in bursts (mousemove fires per frame): re-arm the timer at most this often.
const ACTIVITY_THROTTLE_MS = 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'wheel', 'pointerdown'] as const

const isIdle = ref(false)

let running = false
let idleTimer: ReturnType<typeof setTimeout> | null = null
let lastActivityAt = 0
const { isAndroid, isIOS } = usePlatform()
const isMobileDevice = isAndroid || isIOS

function armIdleTimer() {
  if (idleTimer !== null) clearTimeout(idleTimer)
  idleTimer = setTimeout(() => {
    idleTimer = null
    isIdle.value = true
  }, IDLE_AFTER_MS)
}

function onActivity() {
  // A hidden mobile app can still get stray events; it's away until it comes back to the foreground.
  if (isMobileDevice && document.hidden) return
  const now = Date.now()
  if (!isIdle.value && now - lastActivityAt < ACTIVITY_THROTTLE_MS) return
  lastActivityAt = now
  isIdle.value = false
  armIdleTimer()
}

function onVisibilityChange() {
  if (document.hidden) {
    if (idleTimer !== null) clearTimeout(idleTimer)
    idleTimer = null
    isIdle.value = true
  } else {
    lastActivityAt = Date.now()
    isIdle.value = false
    armIdleTimer()
  }
}

/** Starts watching (idempotent). Called once the user is signed in. */
export function startIdleDetection() {
  if (running || typeof window === 'undefined') return
  running = true
  for (const type of ACTIVITY_EVENTS) window.addEventListener(type, onActivity, { passive: true, capture: true })
  if (isMobileDevice) document.addEventListener('visibilitychange', onVisibilityChange)

  lastActivityAt = Date.now()
  isIdle.value = isMobileDevice && document.hidden
  if (!isIdle.value) armIdleTimer()
}

/** Stops watching and resets to "active" (logout). */
export function stopIdleDetection() {
  if (!running) return
  running = false
  for (const type of ACTIVITY_EVENTS) window.removeEventListener(type, onActivity, { capture: true })
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (idleTimer !== null) clearTimeout(idleTimer)
  idleTimer = null
  isIdle.value = false
}

export function useIdleDetection() {
  return { isIdle: readonly(isIdle) }
}
